import React, {
  useEffect,
  ReactNode,
  useRef,
  useState,
  MouseEvent,
  RefObject,
} from 'react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import IconButton from '@mui/material/IconButton';
import EditIcon from 'core/icons/edit';
import Editable, {
  EditableInstance,
} from 'core/components/Editable';
import Input from 'pages/Dashboard/components/Input';
import Select from 'core/components/Select';
import {
  PdfTemplateSummary,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  useGetFormCategories,
} from 'views/EMR/Settings/PdfTemplateBuilder/hooks';
import {
  FormCategoryDTO,
  FormCategoryRuleDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  formatDateTime,
} from 'utils/date';
import {
  Button,
} from '@populateui/ui';
import {
  isEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import clsx from 'clsx';
import FormAssigneeSelector from 'views/EMR/Settings/PdfTemplateBuilder/components/FormAssigneeSelector';

type Props = {
  formSummary: PdfTemplateSummary;
  category?: FormCategoryDTO | null;
  categoryRule?: FormCategoryRuleDTO | null;
  selectedFormAssignees: FormFieldAssignee[];
  isSaving?: boolean;
  isUpdatingForm?: boolean;
  headerActions?: ReactNode;
  onUpdateFormTitle?: (title: string) => Promise<void>;
  onUpdateFormCategory?: (category: FormCategoryDTO) => Promise<void>;
  onFormAssigneeSelectionChange?: (nextSelection: FormFieldAssignee[]) => void;
  onPreview?: () => Promise<void> | void;
  isPreviewing?: boolean;
  onPublish?: () => Promise<void> | void;
  onSave?: () => Promise<void> | void;
};

export default function TopBar({
  formSummary,
  category,
  categoryRule,
  selectedFormAssignees,
  isSaving,
  isUpdatingForm = false,
  headerActions,
  onUpdateFormTitle,
  onUpdateFormCategory,
  onFormAssigneeSelectionChange,
  onPreview,
  isPreviewing = false,
  onPublish,
  onSave,
}: Props) {
  const { data: formCategories = [] } = useGetFormCategories();
  const editableRef = useRef<EditableInstance>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const pendingTitleSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const [titleDraft, setTitleDraft] = useState(formSummary.title);
  const [categoryDraft, setCategoryDraft] = useState<FormCategoryDTO | null>(category ?? null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'publish' | null>(null);
  const shouldShowDraftSaveButton = formSummary.isDraft;
  let saveButtonLabel = 'Save';
  let publishButtonLabel = 'Save & publish';

  if (pendingAction === 'save' && isSaving) {
    saveButtonLabel = 'Saving...';
  } else if (formSummary.isDraft) {
    saveButtonLabel = 'Save as draft';
  }

  if (pendingAction === 'publish' && isSaving) {
    publishButtonLabel = 'Saving...';
  }

  useEffect(() => {
    setTitleDraft(formSummary.title);
  }, [formSummary.title]);

  useEffect(() => {
    setCategoryDraft(category ?? null);
  }, [category]);

  const saveDraftTitle = async () => {
    if (pendingTitleSavePromiseRef.current) {
      return pendingTitleSavePromiseRef.current;
    }

    const pendingTitleSavePromise = (async () => {
      const nextTitle = titleDraft.trim();

      if (isEmptyString(nextTitle)) {
        setTitleDraft(formSummary.title);
        return false;
      }

      try {
        if (nextTitle !== formSummary.title) {
          await onUpdateFormTitle?.(nextTitle);
        }

        setTitleDraft(nextTitle);
        return true;
      } catch {
        setTitleDraft(formSummary.title);
        return false;
      } finally {
        pendingTitleSavePromiseRef.current = null;
      }
    })();

    pendingTitleSavePromiseRef.current = pendingTitleSavePromise;

    return pendingTitleSavePromise;
  };

  const cancelTitleEdit = () => {
    setTitleDraft(formSummary.title);
    setIsEditingTitle(false);
    editableRef.current?.setIsEdit(false);
  };

  const flushPendingFormMeta = async () => {
    if (!isEditingTitle) {
      return true;
    }

    titleInputRef.current?.blur();
    await Promise.resolve();

    if (pendingTitleSavePromiseRef.current) {
      return pendingTitleSavePromiseRef.current;
    }

    return saveDraftTitle();
  };

  const handleSave = async () => {
    const canContinue = await flushPendingFormMeta();

    if (canContinue) {
      setPendingAction('save');
      try {
        await onSave?.();
      } finally {
        setPendingAction(null);
      }
    }
  };

  const handlePublish = async () => {
    const canContinue = await flushPendingFormMeta();

    if (canContinue) {
      setPendingAction('publish');
      try {
        await onPublish?.();
      } finally {
        setPendingAction(null);
      }
    }
  };

  const handlePreview = async () => {
    const canContinue = await flushPendingFormMeta();

    if (canContinue) {
      await onPreview?.();
    }
  };

  const isBusy = Boolean(isSaving || isUpdatingForm);
  const preventFocusStealWhileEditing = (
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    if (isEditingTitle) {
      event.preventDefault();
    }
  };

  return (
    <header className="w-full bg-white">
      <div className="flex items-start gap-6">
        <div className="min-w-0 flex-1">
          <Editable
            instanceRef={editableRef as RefObject<EditableInstance>}
            controlRef={titleInputRef as RefObject<HTMLElement>}
            tooltip="Edit form name"
            readonly={isUpdatingForm}
            onShowControl={() => {
              setIsEditingTitle(true);
              setTitleDraft(formSummary.title);
            }}
            idleView={(
              <div className="flex min-h-8 w-full max-w-[420px] items-center gap-2">
                <h1 className="max-w-full break-words text-sm font-medium leading-[1.2] text-primary">
                  {formSummary.title}
                </h1>
                <span className="inline-flex shrink-0">
                  <EditIcon className="!h-5 !w-5 text-inherit" />
                </span>
              </div>
            )}
            classes={{
              idleRoot: '!w-full !max-w-[420px] !items-start !rounded-none !p-0 !shadow-none hover:!bg-transparent hover:!shadow-none',
              editableRoot: '!w-full !max-w-[420px] !p-0',
              iconRoot: '!hidden',
            }}
          >
            {(onSubmit, onCancel) => (
              <div className="flex flex-col gap-2">
                <Input
                  id="topbar-form-title"
                  value={titleDraft}
                  inputRef={titleInputRef}
                  className="w-full max-w-[420px]"
                  inputClasses="!text-sm !font-medium"
                  onChange={(value) => {
                    setTitleDraft(value);
                  }}
                  onBlur={async () => {
                    const wasCommitted = await saveDraftTitle();
                    setIsEditingTitle(false);

                    if (wasCommitted) {
                      onSubmit();
                    } else {
                      onCancel();
                    }
                  }}
                  onKeyDown={async (event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();

                      const wasCommitted = await saveDraftTitle();
                      setIsEditingTitle(false);

                      if (wasCommitted) {
                        onSubmit();
                      } else {
                        onCancel();
                      }
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      onCancel();
                      cancelTitleEdit();
                    }
                  }}
                  inputProps={{ maxLength: 64 }}
                />
              </div>
            )}
          </Editable>

          <div className="mt-2 max-w-[220px]">
            <Select<FormCategoryDTO>
              id="topbar-category"
              label={undefined}
              value={categoryDraft ?? undefined}
              options={formCategories}
              selectClassName="!text-left"
              muiClasses={{ select: '!text-left' }}
              getValue={(option) => option.value}
              getLabel={(option) => option.name}
              disabled={isBusy}
              onChange={async (option) => {
                if (Array.isArray(option)) {
                  return;
                }
                try {
                  await onUpdateFormCategory?.(option);
                  setCategoryDraft(option);
                } catch {
                  setCategoryDraft(formCategories.find(
                    (item) => item.value === category?.value,
                  ) ?? null);
                }
              }}
            />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {shouldShowDraftSaveButton ? (
            <Button
              color="primary"
              variant="outlined"
              disabled={isBusy || !onSave}
              onMouseDown={preventFocusStealWhileEditing}
              onClick={() => {
                handleSave();
              }}
            >
              {saveButtonLabel}
            </Button>
          ) : null}
          <Button
            color="primary"
            variant="contained"
            className="!min-w-[84px]"
            disabled={isBusy || !onPublish}
            onMouseDown={preventFocusStealWhileEditing}
            onClick={() => {
              handlePublish();
            }}
          >
            {publishButtonLabel}
          </Button>
          <ToolTip title={isPreviewing ? 'Previewing...' : 'Preview'}>
            <span className="inline-flex !w-8 !h-8">
              <IconButton
                disabled={isBusy || isPreviewing || !onPreview}
                onMouseDown={preventFocusStealWhileEditing}
                onClick={() => {
                  handlePreview();
                }}
                className="!w-8 !h-8 !text-blue-500"
              >
                <PreviewOutlinedIcon className="!h-5 !w-5 !text-inherit" />
              </IconButton>
            </span>
          </ToolTip>
          {headerActions}
        </div>
      </div>

      <div className="mt-4 border-t border-gray-100 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className={clsx(
              'font-medium inline-flex items-center gap-1',
              formSummary.isDraft ? 'text-gray-500' : 'text-green-600',
            )}
            >
              {formSummary.isDraft ? (
                <BuildOutlinedIcon className="!w-4 !h-4" />
              ) : (
                <CheckOutlinedIcon className="!w-4 !h-4" />
              )}
              {formSummary.isDraft ? 'Draft in progress' : 'Published'}
            </span>
            {formSummary.updatedOn ? (
              <>
                <span>•</span>
                <ToolTip title="Last Updated On">
                  <span className="inline-flex items-center gap-1">
                    <ScheduleOutlinedIcon className="!w-4 !h-4" />
                    {formatDateTime(formSummary.updatedOn)}
                  </span>
                </ToolTip>
              </>
            ) : null}
          </div>
          <FormAssigneeSelector
            categoryRule={categoryRule}
            selectedAssignees={selectedFormAssignees}
            onSelectionChange={(nextSelection) => {
              onFormAssigneeSelectionChange?.(nextSelection);
            }}
          />
        </div>
      </div>
    </header>
  );
}
